/** Read projection only. The saved source is never rewritten or truncated. */
export type HumanMarkdownProjection = {
  source: string;
  body: string;
  history: { key: string; body: string }[];
  agentContext: Record<string, unknown> | null;
};
type Range = { start: number; end: number };
type Revision = Range & { bodyStart: number; bodyEnd: number; key: string };
type Line = { text: string; start: number; end: number };
function lines(source: string): Line[] {
  const result: Line[] = []; let offset = 0;
  for (const raw of source.match(/[^\n]*\n|[^\n]+$/g) ?? []) {
    result.push({ text: raw.replace(/\r?\n$/, ""), start: offset, end: offset + raw.length });
    offset += raw.length;
  }
  return result;
}
function without(source: string, ranges: Range[], start = 0, end = source.length): string {
  let cursor = start, result = "";
  for (const range of [...ranges].sort((a, b) => a.start - b.start || b.end - a.end)) {
    if (range.start < start || range.end > end || range.end <= cursor) continue;
    result += source.slice(cursor, Math.max(cursor, range.start)); cursor = range.end;
  }
  return result + source.slice(cursor, end);
}
function contextEnd(source: string, from: number): number {
  let quoted = false, escaped = false;
  for (let offset = from; offset < source.length; offset++) {
    const char = source[offset];
    if (quoted) {
      if (escaped) escaped = false;
      else if (char === "\\") escaped = true;
      else if (char === '"') quoted = false;
    } else if (char === '"') quoted = true;
    else if (source.startsWith("-->", offset)) return offset + 3;
  }
  return source.length;
}
function contains(ranges: Range[], offset: number) {
  return ranges.some((range) => range.start <= offset && range.end > offset);
}
function machineAndCodeRanges(source: string, input: Line[]) {
  const contexts: Range[] = [], code: Range[] = [];
  const byStart = new Map(input.map((line, index) => [line.start, index]));
  let offset = 0;
  while (offset < source.length) {
    const lineIndex = byStart.get(offset);
    if (lineIndex !== undefined) {
      const line = input[lineIndex];
      const fence = /^ {0,3}(`{3,}|~{3,})(.*)$/.exec(line.text);
      if (fence && !(fence[1][0] === "`" && fence[2].includes("`"))) {
        let end = source.length;
        for (let index = lineIndex + 1; index < input.length; index++) {
          const close = /^ {0,3}(`{3,}|~{3,})[ \t]*$/.exec(input[index].text);
          if (close && close[1][0] === fence[1][0] && close[1].length >= fence[1].length) { end = input[index].end; break; }
        }
        code.push({ start: offset, end }); offset = end; continue;
      }
      if (/^(?: {4}|\t)/.test(line.text)) {
        code.push({ start: offset, end: line.end }); offset = line.end; continue;
      }
    }
    if (source[offset] === "`") {
      let slashes = 0;
      for (let previous = offset - 1; previous >= 0 && source[previous] === "\\"; previous--) slashes++;
      if (slashes % 2 === 1) { offset++; continue; }
      let length = 1; while (source[offset + length] === "`") length++;
      const rest = source.slice(offset + length);
      const paragraphBoundary = /\r?\n[ \t]*\r?\n/.exec(rest);
      const paragraphEnd = paragraphBoundary ? offset + length + paragraphBoundary.index : source.length;
      let next = offset + length, end = -1;
      while ((next = source.indexOf("`", next)) >= 0 && next < paragraphEnd) {
        let closingLength = 1; while (source[next + closingLength] === "`") closingLength++;
        if (closingLength === length) { end = next + closingLength; break; }
        next += closingLength;
      }
      if (end >= 0) { code.push({ start: offset, end }); offset = end; continue; }
      offset += length; continue;
    }
    const opening = source.startsWith("<!--", offset) ? /^<!--\s*pauta:agent-context:v1\b/.exec(source.slice(offset)) : null;
    if (opening) {
      const end = contextEnd(source, offset + opening[0].length);
      contexts.push({ start: offset, end }); offset = end; continue;
    }
    offset++;
  }
  return { contexts, code };
}
function scan(source: string) {
  const input = lines(source);
  const { contexts, code } = machineAndCodeRanges(source, input);
  const markers: Range[] = [], revisions: Revision[] = [];
  const stack: { kind: string; key: string; start: number; bodyStart: number }[] = [];
  let agentContext: Record<string, unknown> | null = null;
  for (const range of contexts) {
    const opening = /^<!--\s*pauta:agent-context:v1\b/.exec(source.slice(range.start))!;
    const bodyEnd = source.slice(range.end - 3, range.end) === "-->" ? range.end - 3 : range.end;
    try { const parsed = JSON.parse(source.slice(range.start + opening[0].length, bodyEnd)); if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) agentContext = parsed; } catch { /* Keep source intact; machine payload never becomes prose. */ }
  }
  for (let index = 0; index < input.length; index++) {
    const line = input[index];
    if (contains(code, line.start) || contains(contexts, line.start)) continue;
    const start = /^<!-- pauta:previous-(content|description):start sha256=([a-f0-9]{64}) -->$/.exec(line.text);
    if (start) {
      let first = index - 1; while (first >= 0 && !input[first].text.trim()) first--;
      const heading = input[first];
      stack.push({ kind: start[1], key: start[2], start: heading?.text === "### 🕰️ Versão anterior preservada" && !contains(code, heading.start) ? heading.start : line.start, bodyStart: line.end });
    } else {
      const end = /^<!-- pauta:previous-(content|description):end -->$/.exec(line.text);
      if (end && stack.at(-1)?.kind === end[1]) {
        const begin = stack.pop()!; revisions.push({ ...begin, end: line.end, bodyEnd: line.start });
      }
    }
    for (const marker of line.text.matchAll(/<!--\s*pauta:[^\r\n]*?-->/g)) {
      if (!contains(code, line.start + marker.index!) && !contains(contexts, line.start + marker.index!)) markers.push({ start: line.start + marker.index!, end: line.start + marker.index! + marker[0].length });
    }
  }
  return { contexts, code, markers, revisions, agentContext };
}
const trimProjectedBody = (body: string) => body.trim()
  ? body.replace(/^(?:[ \t]*\r?\n)+/, "").trimEnd()
  : "";

export function projectHumanMarkdown(source: string | null | undefined): HumanMarkdownProjection {
  const saved = source ?? ""; const { contexts, markers, revisions, agentContext } = scan(saved);
  const history: HumanMarkdownProjection["history"] = []; const seen = new Set<string>();
  for (const revision of revisions.sort((a, b) => a.start - b.start)) {
    const body = without(saved, [...contexts, ...markers, ...revisions.filter((candidate) => candidate !== revision)], revision.bodyStart, revision.bodyEnd).trim();
    if (!body || seen.has(body)) continue;
    seen.add(body); history.push({ key: `${revision.key}-${history.length}`, body });
  }
  return { source: saved, body: trimProjectedBody(without(saved, [...contexts, ...markers, ...revisions])), history, agentContext };
}

export type ReadingMarkdownProjection = HumanMarkdownProjection & {
  generated: boolean;
  presentationChanged: boolean;
  originalBody: string;
};

const defaultDocPurpose = "Use este documento como ponto de entrada; o conteúdo integral e as evidências permanecem abaixo.";
const normalizedText = (value: string) => value.trim().replace(/\s+/g, " ");

// Only edge emoji separated from the text can decorate a generated heading.
// Preserve attached symbols, punctuation and every distinct authored title.
const emojiPoint = String.raw`(?![©®™])[\p{Extended_Pictographic}\p{Regional_Indicator}]`;
const edgeEmoji = String.raw`${emojiPoint}(?:${emojiPoint}|[\p{Emoji_Modifier}\uFE0F\u200D])*`;
const leadingTitleDecoration = new RegExp(String.raw`^(?:${edgeEmoji}\s+)+`, "u");
const trailingTitleDecoration = new RegExp(String.raw`(?:\s+${edgeEmoji})+$`, "u");
const normalizedReadingTitle = (value: string) => normalizedText(value)
  .replace(leadingTitleDecoration, "")
  .replace(trailingTitleDecoration, "");

/** Condense only the exact, marked document envelope produced by Pauta.
 * Authored headings, the saved source and historical versions remain intact. */
export function projectReadingMarkdown(source: string | null | undefined, title: string): ReadingMarkdownProjection {
  const projection = projectHumanMarkdown(source);
  const unchanged = { ...projection, generated: false, presentationChanged: false, originalBody: projection.body };
  const saved = projection.source;
  const { code, contexts, revisions } = scan(saved);
  const protectedRanges = [...code, ...contexts, ...revisions];
  const starts = [...saved.matchAll(/^<!-- pauta:original-(content|description):start sha256=([a-f0-9]{64}) -->\r?$/gm)]
    .filter((match) => !contains(protectedRanges, match.index!));
  if (starts.length !== 1) return unchanged;
  const start = starts[0];
  const end = [...saved.matchAll(/^<!-- pauta:original-(content|description):end -->\r?$/gm)]
    .find((match) => match[1] === start[1] && match.index! > start.index! && !contains(protectedRanges, match.index!));
  if (!end) return unchanged;
  const signature = [...saved.matchAll(/^<!-- pauta:human-first:v1 source_sha256=([a-f0-9]{64}) -->\r?$/gm)]
    .find((match) => match.index! > end.index! && match[1] === start[2] && !contains(protectedRanges, match.index!));
  if (!signature) return unchanged;
  const prefix = saved.slice(0, start.index!);
  const docEnvelope = /^\s*## 🧭 Em poucas palavras\r?\n\s*\r?\n([\s\S]*?)\r?\n\s*\r?\n## 💡 Como usar este documento\r?\n\s*\r?\n([\s\S]*?)\r?\n\s*\r?\n## 🧩 Conteúdo completo, evidências e histórico preservados\s*$/.exec(prefix);
  const issueEnvelope = /^\s*## 🧭 Em poucas palavras\r?\n\s*\r?\n([\s\S]*?)\r?\n\s*\r?\n(## 💡 Por que isso importa\r?\n[\s\S]*?\r?\n## 🎯 Resultado esperado\r?\n[\s\S]*?\r?\n## 📍 Situação atual\r?\n[\s\S]*?\r?\n## ✅ Como saber que terminou\r?\n[\s\S]*?\r?\n## 🔗 Contexto rastreável\r?\n[\s\S]*?)\r?\n\s*\r?\n## 🧩 Detalhes, evidências e histórico preservados\s*$/.exec(prefix);
  const envelope = start[1] === "content" ? docEnvelope : issueEnvelope;
  if (!envelope || protectedRanges.some((range) => range.start < start.index! && range.end > 0)) return unchanged;
  const original = projectHumanMarkdown(saved.slice(start.index! + start[0].length, end.index!)).body;
  let body = original;
  // Strip the original title only inside a verified generated envelope. A plain
  // authored document with an identical H1 keeps that H1.
  const h1 = /^(?: {0,3}# ([^\r\n]+)| {0,3}([^ \t\r\n][^\r\n]*)\r?\n {0,3}=+[ \t]*)\r?\n?/.exec(body);
  const headingTitle = h1 && normalizedReadingTitle((h1[1] ?? h1[2]).replace(/[ \t]+#+[ \t]*$/, ""));
  const readingTitle = normalizedReadingTitle(title);
  if (h1 && readingTitle && headingTitle === readingTitle) body = trimProjectedBody(body.slice(h1[0].length));
  const summary = projectHumanMarkdown(envelope[1]).body;
  const purpose = projectHumanMarkdown(envelope[2]).body;
  const firstParagraph = body.split(/\r?\n\s*\r?\n/)[0];
  const intro = summary && normalizedText(summary) !== normalizedText(title) && normalizedText(summary) !== normalizedText(firstParagraph) ? summary : "";
  const usefulPurpose = purpose && (start[1] === "description" || (normalizedText(purpose) !== defaultDocPurpose && normalizedText(purpose) !== normalizedText(summary))) ? purpose : "";
  const suffix = projectHumanMarkdown(saved.slice(end.index! + end[0].length)).body;
  const readingBody = [intro, usefulPurpose, body, suffix].filter(Boolean).join("\n\n");
  return { ...projection, body: readingBody, generated: true, presentationChanged: readingBody !== projection.body, originalBody: projection.body };
}

/** Text eligible for explicit references: current human prose, excluding code. */
export function currentHumanReferenceText(source: string | null | undefined): string {
  const body = source ?? "";
  const { code, contexts, markers, revisions } = scan(body);
  let cursor = 0, result = "";
  for (const range of [...code, ...contexts, ...markers, ...revisions].sort((a, b) => a.start - b.start || b.end - a.end)) {
    if (range.end <= cursor) continue;
    result += body.slice(cursor, Math.max(cursor, range.start)) + body.slice(Math.max(cursor, range.start), range.end).replace(/[^\r\n]/g, " ");
    cursor = range.end;
  }
  return (result + body.slice(cursor)).replace(/<!--[\s\S]*?(?:-->|$)/g, (comment) => comment.replace(/[^\r\n]/g, " "));
}
/** Current-preview image edits must not rewrite historical or machine blocks. */
export function replaceCurrentHumanMarkdown(source: string, oldValue: string, newValue: string): string {
  if (!oldValue) return source;
  const { contexts, markers, revisions } = scan(source);
  const ranges = [...contexts, ...markers, ...revisions].sort((a, b) => a.start - b.start || b.end - a.end);
  let cursor = 0, result = "";
  for (const range of ranges) {
    if (range.end <= cursor) continue;
    result += source.slice(cursor, Math.max(cursor, range.start)).split(oldValue).join(newValue);
    result += source.slice(Math.max(cursor, range.start), range.end); cursor = range.end;
  }
  return result + source.slice(cursor).split(oldValue).join(newValue);
}
